import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { WhatsAppCTA } from "../WhatsAppCTA";
import { WHATSAPP_MESSAGES, IMAGES } from "../../lib/site";

const HEADLINE = ["Seu celular", "de volta à rotina."];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: {
      duration: 1,
      delay: 0.3 + i * 0.15,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const imgOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -120]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden flex items-center justify-center"
    >
      {/* Background image with parallax scale */}
      <motion.div style={{ scale, opacity: imgOpacity }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Celular premium em fundo escuro"
          loading="eager"
          className="w-full h-full object-cover object-center"
        />
      </motion.div>

      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/40 via-transparent to-[#050505]" />
      <div className="absolute inset-0 spotlight" />

      {/* Text */}
      <motion.div
        style={{ y: textY }}
        className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 text-center"
      >
        <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold tracking-tighter leading-[0.95] text-[#f5f5f7] glow-text">
          {HEADLINE.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className="block"
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-7 text-base md:text-lg text-[#f5f5f7] max-w-xl mx-auto"
        >
          Atendimento Presencial em Vitória da Conquista - BA
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1 }}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <WhatsAppCTA
            label="Falar no WhatsApp"
            message={WHATSAPP_MESSAGES.main}
            pulse
            icon={<MessageCircle size={18} />}
            testId="hero-whatsapp-primary"
          />
          <WhatsAppCTA
            label="Agendar avaliação"
            message={WHATSAPP_MESSAGES.schedule}
            variant="secondary"
            testId="hero-whatsapp-secondary"
          />
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <div className="w-6 h-10 rounded-full border border-white/20 flex items-start justify-center pt-2">
          <span className="w-1 h-2 rounded-full bg-white/60 scroll-dot" />
        </div>
      </motion.div>
    </section>
  );
};
